import { z } from 'zod'

import { ObFeeMockValuez, PersonNames, UserNewZ, UserStatus } from '@/types/userNew'

import { CarLoyaltyProgramCard } from './car'
import { CountryType, Gender, RelativeURL, UserCurrency, UserId, UserIdZ, WithUserId } from './common'
import { WithCreditCards } from './creditCard'
import { NationalId, Passport } from './document'
import { FrequentFlyer } from './flights/common'
import { Group } from './group'
import { HotelLoyaltyProgramCard } from './hotel'
import { InvoiceProfile } from './invoiceProfile'
import { getJobGroupResultZ } from './job'
import { Locales } from './locale'
import { Location } from './location'
import { PermissionRole, UserPermissionsTypes } from './permissions'
import type { UserRailCard } from './rails/common'

export const UserMiniZ = UserNewZ.pick({
	deactivate_at: true,
	deleted: true,
	first_name: true,
	id: true,
	image_url: true,
	last_name: true,
	middle_name: true,
	status: true,
})

export const SetIsMockEnabledRequestZ = UserNewZ.pick({
	mock_enabled: true,
}).required()

export const SetIsTokenizerMockEnabledRequestZ = UserNewZ.pick({
	tokenizer_mock_enabled: true,
}).required()

export const SetIsWorldlineMockEnabledRequestZ = UserNewZ.pick({
	worldline_mock_enabled: true,
}).required()

export const SetIsFlightSearchMockEnabledRequestZ = UserNewZ.pick({
	flight_search_mock_enabled: true,
}).required()

export const SetIsObFeeMockEnabledRequestZ = z
	.strictObject({
		ob_fee_mock: ObFeeMockValuez.nullable(),
	})
	.required()

export const InvitePendingUsersSuccessZ = getJobGroupResultZ(
	z.strictObject({
		user_id: UserIdZ,
		message: z.string(),
	}),
)

export interface UserRole extends WithUserId {
	id?: number
	role_id: number
	role?: PermissionRole
}

export interface UserPermission {
	roles: UserPermissionsTypes[]
}
export interface UserPermission extends WithUserId {
	id: number
	permission: UserPermissionsTypes
	enabled: boolean
}

// eslint-disable-next-line no-restricted-syntax
export enum Role {
	Employee = 'employee',
	Manager = 'manager',
	Executive = 'executive',
}

export interface TravelProfile extends WithUserId, WithCreditCards {
	id: User['id']
	role: Role
	date_of_birth: string | null
	employee_id: number | string | null
	gender: Gender | null
	home_location?: Location | null
	office?: Location | null
	home_location_id?: Location['id']
	office_id?: Location['id']
	emergency_contact_name: string | null
	emergency_contact_phone_number: string | null
	emergency_contact_email: string | null
	has_medical_condition: boolean
	flight_properties: object
	phone_number: string
	dial_code_country: CountryType
	frequent_flyer_cards?: FrequentFlyer[]
	hotel_loyalty_programs?: HotelLoyaltyProgramCard[]
	car_loyalty_programs?: CarLoyaltyProgramCard[]
	passports?: Passport[]
	national_ids?: NationalId[]
	title: string | null
	group?: Group | null
	group_id?: Group['id'] | null
	rail_cards: UserRailCard[]
	cost_center: string | null
	accounting_unit: string | null
	internal_account: string | null
	purchase_order_number: string | null
	custom_field_1: string | null
	custom_field_2: string | null
	custom_field_3: string | null
	kt_number: string | null
	ca_number: string | null
	redress_number: string | null
}

export type User = PersonNames & {
	id: UserId
	email: string
	traveler_profile?: TravelProfile
	user_roles?: UserRole[]
	user_permissions?: UserPermission[]
	image_url?: string | RelativeURL | null
	bookable?: boolean
	auto_select_self: boolean
	language_code: Locales | null
	currency_code: UserCurrency
	gender?: Gender | null
	invoice_profile?: InvoiceProfile | null
	mock_enabled?: boolean
	tokenizer_mock_enabled?: boolean
	worldline_mock_enabled?: boolean
	flight_search_mock_enabled?: boolean
	is_admin?: boolean
	cc_special_access?: boolean
	deactivate_at: Date | string | null
	deleted: boolean
	status: UserStatus
	rail_cards?: UserRailCard[]
} & Partial<z.infer<typeof SetIsObFeeMockEnabledRequestZ>>

export type UserKeys = keyof User
